import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { exceptionNotificationAPI } from "../services/api";
import moment from "moment";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom"; // Se usar react-router para redirecionar no logout

const AuthContext = createContext({});

const AUTH_TOKEN_KEY = "@ScheduleNogleApp:token";
const AUTH_USER_KEY = "@ScheduleNogleApp:user";
const AUTH_USERNAME_KEY = "@ScheduleNogleApp:userName";

// ✅ Unificado: define onde vamos buscar/salvar por padrão (Session = perde ao fechar aba)
const storage = sessionStorage;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutTimerRef = useRef(null);

  // Interceptor ID ref para eject on unmount
  const interceptorId = useRef(null);

  // ---------- Helpers ----------

  const clearStorage = useCallback(() => {
    // Limpa ambos para garantir migração e logout total
    try {
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
      sessionStorage.removeItem(AUTH_USER_KEY);
      sessionStorage.removeItem(AUTH_USERNAME_KEY);

      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_KEY);
      localStorage.removeItem(AUTH_USERNAME_KEY);
    } catch { /* ignore */ }

    // Header limpo é responsabilidade do interceptor, mas podemos garantir aqui também
    api.defaults.headers.authorization = "";
  }, []);

  const clearTimer = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }, []);

  const scheduleAutoSignOut = useCallback((token) => {
    clearTimer();
    try {
      const decoded = jwtDecode(token);
      if (!decoded?.exp) return;

      const msUntilExpire = decoded.exp * 1000 - Date.now();

      if (msUntilExpire <= 0) {
        // Já expirou
        signOut();
        return;
      }

      // Agenda logout
      logoutTimerRef.current = setTimeout(() => {
        signOut();
      }, msUntilExpire);
    } catch (err) {
      console.warn("Invalid token for auto-logout schedule", err);
    }
  }, [clearTimer]);

  // ---------- Actions ----------

  const signOut = useCallback(async () => {
    clearTimer();
    clearStorage();

    // Pequeno delay visual se desejar, ou imediato
    setUser(null);
    setLoading(false);

    // O redirecionamento acontece reativamente pois user vira null
  }, [clearTimer, clearStorage]);

  const signIn = async ({ email, password }) => {
    clearTimer();
    setLoading(true);

    try {
      let response;
      let r;
      let token;

      try {
        // 1. Tenta o login tradicional do sistema Cartman
        response = await api.post("/authenticate", { email, password });
        r = response.data;
        token = r.token;
      } catch (err) {
        // Se falhar (401/404), tenta o login específico da Agenda (Fallback)
        if (err.response?.status === 401 || err.response?.status === 404) {
          try {
            response = await api.post("/comercial/login", { email, password });
            r = response.data;
            token = r.token;
          } catch (localErr) {
            // O /comercial/login diferencia os casos:
            //   404 → usuário não cadastrado: registra a solicitação de acesso
            //         (cria linha pendente em USUARIOS_MONITOR_NOGLE, permissions=0)
            //   403 → cadastrado mas pendente/inativo (aguarda liberação)
            //   401 → cadastrado e ativo, senha incorreta
            const status = localErr.response?.status;
            api.defaults.headers.authorization = "";
            setLoading(false);

            if (status === 404) {
              try {
                await api.post("/comercial/solicitar-acesso", { email, password });
              } catch (_) { /* best-effort */ }
              return { error: "Sua conta foi registrada e aguarda a liberação do administrador." };
            }
            if (status === 403) {
              return { error: localErr.response?.data?.message || "Sua conta aguarda a liberação do administrador." };
            }
            if (status === 401) {
              return { error: "Verifique as informações preenchidas." };
            }
            return { error: "Não foi possível entrar. Tente novamente." };
          }
        } else {
          throw err;
        }
      }

      // 1. Validações básicas e Whitelist
      if (!token) throw new Error("Token não recebido.");
      api.defaults.headers.authorization = `Bearer ${token}`;

      try {
        await api.get("/comercial/me");
      } catch (err) {
        if (err?.response?.status === 403) {
          api.defaults.headers.authorization = "";
          setLoading(false);
          return { error: "Acesso negado (Whitelist)." };
        }
        throw err;
      }

      // 3. Sucesso: Salvar e Agendar
      storage.setItem(AUTH_TOKEN_KEY, token);
      storage.setItem(AUTH_USER_KEY, JSON.stringify(r));
      storage.setItem(AUTH_USERNAME_KEY, email);

      setUser({ token, user: r });
      scheduleAutoSignOut(token);

      setLoading(false);
      return response;

    } catch (error) {
      clearStorage();
      setLoading(false);

      if (error.response?.status === 401) {
        return { error: error.response.data.message || "Credenciais inválidas." };
      }
      if (error.response?.status === 403) {
        return { error: "Acesso bloqueado." };
      }

      exceptionNotificationAPI(error);
    }
  };

  // ---------- Effects ----------

  // 1. Interceptor Global 401 (Executa 1 vez)
  useEffect(() => {
    interceptorId.current = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          // Token inválido/expirado no backend -> Force logout
          signOut();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      if (interceptorId.current !== null) {
        api.interceptors.response.eject(interceptorId.current);
        interceptorId.current = null;
      }
    };
  }, [signOut]);

  // 2. Init App (Restore Session)
  useEffect(() => {
    const token = storage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(AUTH_TOKEN_KEY);
    const userStored = storage.getItem(AUTH_USER_KEY) || localStorage.getItem(AUTH_USER_KEY);

    if (token && userStored) {
      try {
        // Verifica validade síncrona/rapida
        const decoded = jwtDecode(token);
        const isExpired = decoded.exp * 1000 <= Date.now();

        if (!isExpired) {
          api.defaults.headers.authorization = `Bearer ${token}`; // opcional, já que interceptor cuida
          setUser({ token, user: JSON.parse(userStored) });
          scheduleAutoSignOut(token);
        } else {
          signOut(); // Expirado
        }
      } catch (e) {
        signOut(); // Token corrompido
      }
    } else {
      signOut(); // Sem sessão
    }

    setLoading(false);
  }, [scheduleAutoSignOut, signOut]);

  // ---------- Extras Legacy (Header Expand) ----------
  const [headerExpanded, setHeaderExpanded] = useState(null);
  const headerExpand = () => {
    const val = moment().toISOString();
    localStorage.setItem("@ScheduleNogleApp:isExpanded", val);
    setHeaderExpanded(val);
  };
  const [faturamentoOpen, setFaturamentoOpen] = useState(false);
  const faturamentoOpenModal = () => setFaturamentoOpen(!faturamentoOpen);

  return (
    <AuthContext.Provider
      value={{
        user: user?.user,
        loading,
        signIn,
        signOut,
        headerExpand,
        headerExpanded,
        faturamentoOpenModal,
        faturamentoOpen,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}
